import { useState, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, ArrowRightLeft, Info } from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { formatCustomerName } from "@/lib/clientUtils";
import { findOptimalTable, getOptimalityScore } from "@/lib/tableAssignmentUtils";
import { ConflictCheckOptions } from "@/lib/reservationConflictUtils";
import { isPhysicalOverlapError, getConflictToastMessage } from "@/lib/reservationConflictError";
import type { Reservation, Table, CombinedTable, TableAvailability, TableWeekdayAvailability, TableBookablePeriod } from "@/types/table";

interface TerraceClosureImpactDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  date: string;
  timeSlotId: string | null;
  reservations: Reservation[];
  establishmentId: string;
  onConfirm: () => void;
  // Table assignment props
  tables: Table[];
  combinedTables: CombinedTable[];
  allReservations: Reservation[];
  availabilities: TableAvailability[];
  weekdayAvailabilities: TableWeekdayAvailability[];
  bookablePeriods: TableBookablePeriod[];
  childrenCapacityMode: 'full' | 'half' | 'none';
  durationMinutes: number;
  customAssignmentOrderEnabled?: boolean;
  /** 'replace' : terrasse désactivée, replacer les réservations à venir (plusieurs dates). */
  mode?: 'closure' | 'replace';
}

export function TerraceClosureImpactDialog({
  open,
  onOpenChange,
  date,
  timeSlotId,
  reservations,
  establishmentId,
  onConfirm,
  tables,
  combinedTables,
  allReservations,
  availabilities,
  weekdayAvailabilities,
  bookablePeriods,
  childrenCapacityMode,
  durationMinutes,
  customAssignmentOrderEnabled = false,
  mode = 'closure',
}: TerraceClosureImpactDialogProps) {
  const [reassigning, setReassigning] = useState<Set<string>>(new Set());
  const [reassigned, setReassigned] = useState<Set<string>>(new Set());
  const [sessionAssignments, setSessionAssignments] = useState<
    Map<string, { tableId: string | null; combinedTableId: string | null }>
  >(new Map());

  // Pre-compute optimal indoor table for each reservation
  const tableProposals = useMemo(() => {
    const proposals = new Map<string, { type: 'single' | 'combined'; id: string; capacity: number; name: string } | null>();
    
    reservations.forEach(r => {
      const conflictOptions: ConflictCheckOptions = {
        reservationDurationMinutes: r.durationMinutes ?? durationMinutes,
      };
      // Exclude current reservation from conflict checks, and apply in-session
      // reassignments so a table already picked earlier in this dialog session
      // is treated as occupied for subsequent proposals.
      const otherReservations = allReservations
        .filter(other => (other.supabaseId || other.id) !== (r.supabaseId || r.id))
        .map(other => {
          const override = sessionAssignments.get(other.supabaseId || other.id);
          if (!override) return other;
          return { ...other, tableId: override.tableId, combinedTableId: override.combinedTableId };
        });

      const proposed = findOptimalTable(
        r.numberOfPeople,
        r.date,
        r.startTime,
        tables,
        combinedTables,
        otherReservations,
        availabilities,
        weekdayAvailabilities,
        conflictOptions,
        bookablePeriods,
        r.numberOfChildren ?? 0,
        childrenCapacityMode,
        r.hasStroller ?? false,
        'indoor',
        undefined,
        customAssignmentOrderEnabled
      );

      proposals.set(r.id, proposed);
    });

    return proposals;
  }, [reservations, allReservations, sessionAssignments, tables, combinedTables, availabilities, weekdayAvailabilities, bookablePeriods, childrenCapacityMode, durationMinutes, customAssignmentOrderEnabled]);

  const handleReassign = async (reservation: Reservation) => {
    setReassigning(prev => new Set(prev).add(reservation.id));
    try {
      const proposedTable = tableProposals.get(reservation.id);
      const updateData: Record<string, unknown> = { terrace_preference: 'indoor' };
      
      if (proposedTable) {
        if (proposedTable.type === 'single') {
          updateData.table_id = proposedTable.id;
          updateData.combined_table_id = null;
        } else {
          updateData.combined_table_id = proposedTable.id;
          updateData.table_id = null;
        }
      }

      const { error } = await supabase
        .from('reservations')
        .update(updateData)
        .eq('id', reservation.supabaseId || reservation.id);
      
      if (error) throw error;
      
      const tableName = proposedTable?.name;
      // Track this assignment locally so subsequent proposals in the same
      // dialog session exclude the physical table(s) we just used.
      setSessionAssignments(prev => {
        const next = new Map(prev);
        next.set(reservation.supabaseId || reservation.id, {
          tableId: proposedTable?.type === 'single' ? proposedTable.id : null,
          combinedTableId: proposedTable?.type === 'combined' ? proposedTable.id : null,
        });
        return next;
      });
      setReassigned(prev => new Set(prev).add(reservation.id));
      toast({ 
        title: "Réassigné", 
        description: tableName
          ? `${formatCustomerName(reservation.customerFirstName, reservation.customerLastName)} → ${tableName}`
          : `${formatCustomerName(reservation.customerFirstName, reservation.customerLastName)} déplacé en intérieur`
      });
    } catch (err) {
      if (isPhysicalOverlapError(err)) {
        const { title, description } = getConflictToastMessage();
        toast({ title, description, variant: "destructive" });
      } else {
        toast({ title: "Erreur", description: "Impossible de réassigner cette réservation.", variant: "destructive" });
      }
    } finally {
      setReassigning(prev => {
        const next = new Set(prev);
        next.delete(reservation.id);
        return next;
      });
    }
  };


  const handleConfirmAndClose = () => {
    onConfirm();
    setReassigned(new Set());
    setSessionAssignments(new Map());
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-orange-500" />
            {mode === 'replace' ? 'Réservations encore sur la terrasse' : 'Réservations terrasse impactées'}
          </DialogTitle>
          <DialogDescription>
            {mode === 'replace'
              ? `La terrasse est désactivée. ${reservations.length} réservation${reservations.length > 1 ? 's' : ''} à venir ${reservations.length > 1 ? 'sont' : 'est'} encore sur la terrasse. Rien n'est déplacé sans votre clic, aucun e-mail n'est envoyé.`
              : `${reservations.length} réservation${reservations.length > 1 ? 's' : ''} terrasse pour le ${format(new Date(date), 'd MMMM yyyy', { locale: fr })}`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 max-h-[50vh] overflow-y-auto">
          {reservations.map(r => {
            const proposed = tableProposals.get(r.id);
            const score = proposed ? getOptimalityScore(proposed.capacity, r.numberOfPeople) : null;
            return (
              <div key={r.id} className="flex items-center justify-between p-2 rounded-lg border gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{r.customerFirstName} {r.customerLastName}</p>
                  <p className="text-xs text-muted-foreground">
                    {mode === 'replace' && <>{format(new Date(r.date), 'EEE d MMM', { locale: fr })} · </>}{r.startTime.substring(0, 5)} — {r.numberOfPeople} pers.
                  </p>
                  {proposed && !reassigned.has(r.id) && (
                    <p className="text-xs text-muted-foreground">
                      → {proposed.name} ({proposed.capacity} pl.)
                    </p>
                  )}
                  {!proposed && !reassigned.has(r.id) && (
                    <p className="text-xs text-destructive">{mode === 'replace' ? 'Aucune table intérieure libre : à traiter à la main' : 'Aucune table dispo'}</p>
                  )}
                </div>
                {reassigned.has(r.id) ? (
                  <Badge variant="success" className="text-xs shrink-0">Réassigné</Badge>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-xs shrink-0"
                    disabled={reassigning.has(r.id) || (mode === 'replace' && !proposed)}
                    onClick={() => handleReassign(r)}
                  >
                    <ArrowRightLeft className="w-3 h-3 mr-1" />
                    {proposed ? proposed.name : 'Intérieur'}
                  </Button>
                )}
              </div>
            );
          })}
        </div>

        {reservations.length > 0 && (
          <div className="flex items-start gap-2 p-3 rounded-lg bg-muted/50 text-xs text-muted-foreground">
            <Info className="w-4 h-4 mt-0.5 shrink-0" />
            <span>{mode === 'replace'
              ? "Pour choisir une autre table ou prévenir le client, ouvrez la réservation depuis la vue du jour."
              : "Pour choisir une table spécifique et envoyer un email au client, utilisez l'onglet Terrasse après la fermeture."}</span>
          </div>
        )}

        <DialogFooter>
          <Button onClick={handleConfirmAndClose} className="w-full sm:w-auto">
            {mode === 'replace' ? 'Terminer' : 'Fermer la terrasse'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
